import { skipToken } from "@reduxjs/toolkit/query";
import { MAX_TRANSCRIPT_SEARCH_CHARS } from "@repo/app-types/agents/customerInteractionFilters";
import {
  FuzzySearchResourceType,
  readFuzzySearchDurationSeconds,
  type FuzzySearchResult,
} from "@repo/app-types/entities/search";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { ShortAbsoluteTimestamp } from "@repo/ui/timestamps/shortAbsoluteTimestamp";
import {
  IconBook2,
  IconPhone,
  IconSearch,
  IconUser,
} from "@tabler/icons-react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router";
import { useAsyncList } from "react-stately";
import { useAllMinimalPlaybooksQuery } from "../../api/entities/playbook.js";
import {
  useLazyFuzzySearchQuery,
  useLazyTeamPerformanceSearchQuery,
  useSearchResourcesQuery,
} from "../../api/entities/search.js";
import { appPaths } from "../../app/appPaths.js";
import { focusMainContent } from "../../app/mainContent.js";
import { useAppSelector } from "../../hooks/hook.js";
import { usePlaybookIdParam } from "../../hooks/usePlaybookIdParam.js";
import { selectCompany } from "../company/companySlice.js";
import { DEFAULT_PLAYBOOK_URL_SEGMENT } from "../links/defaultPlaybookRedirect.js";
import { renderCallDuration } from "../teammatePerformance/durationFormatting.js";
import { buildTranscriptSearchApplyState } from "../teammatePerformance/hooks/useTranscriptSearch.js";
import {
  SearchModal,
  type SearchModalAction,
  type SearchModalResult,
  type SearchModalSectionData,
} from "./components/searchModal.js";
import {
  buildKeyForSearchResult,
  buildPathForSearchResult,
} from "./helpers.js";
import {
  readRecentlyOpenedResultKeys,
  readRecentSearchQueries,
  recordRecentlyOpenedResult,
  recordRecentSearchQuery,
} from "./searchHistoryStorage.js";

export interface SearchDialogProps {
  placeholder: string;
  initialSearchQuery: string | undefined;
  resourceTypes: readonly string[] | undefined;
  isTrainingSearchContext: boolean;
  /**
   * Enables the transcript keyword-search affordances — the "Search all calls"
   * entry point and the recent keyword searches that replay it. Only the team
   * performance context has a calls list to search.
   */
  showTranscriptKeywordSearch: boolean;
  /**
   * Searches team performance, which groups its results into a People and a
   * Calls section. Every other context searches its resource types at once and
   * shows the matches as one list.
   */
  isTeamPerformanceSearchContext: boolean;
  /**
   * Sends the "Search all calls" entry point to this teammate's calls page
   * instead of the playbook-wide one. Decided by `useSearchDialogContext`.
   */
  transcriptSearchTeammateId: TeammateId | undefined;
  onSearchResultsLoad: (searchResults: readonly FuzzySearchResult[]) => void;
  /** Supplied by `SearchModalOverlay` (not callers) to dismiss the modal. */
  close: () => void;
}

const searchQueryDebounceDuration = 300;

/**
 * How long results that no longer answer the query stay on screen before the
 * skeleton takes over. Covers the debounce plus a quick response, so ordinary
 * typing doesn't flash the skeleton while a slow search still says it is
 * working.
 */
const staleResultsGraceDuration = 500;

/** History entries shown when the search opens without a query. */
const visibleHistoryEntryCount = 3;

/**
 * Results per section of the team performance search. Sent as the limit of each
 * section's search, so a section shows everything it is given.
 */
const teamPerformanceResultCountPerSection = 3;

interface SearchResultSection {
  id: string;
  title: string;
  resourceType: FuzzySearchResourceType;
  /** Row shape the section's skeleton stands in for. */
  hasBadge?: boolean;
  hasTrailing?: boolean;
}

/**
 * The sections of the team performance search, in a fixed order: only which
 * results land in a section changes as the user types, never the sections
 * themselves. Each is searched separately, so one can't crowd out the other.
 */
const teamPerformanceSections: readonly SearchResultSection[] = [
  {
    id: "people",
    title: "People",
    resourceType: FuzzySearchResourceType.TeammateWithGradings,
    hasBadge: true,
  },
  {
    id: "calls",
    title: "Calls",
    resourceType: FuzzySearchResourceType.TeammateAccountGrading,
    hasTrailing: true,
  },
];

export const SearchDialog: FC<SearchDialogProps> = (props) => {
  const [searchQuery, setSearchQuery] = useState(
    props.initialSearchQuery ?? "",
  );
  const companyId = useAppSelector(selectCompany)?.id;

  // The playbook the search is being run from, which decides both which
  // playbook's calls the all-calls action searches and, for a teammate found in
  // several, which playbook's page their result opens.
  const currentPlaybookId = usePlaybookIdParam();

  const [performFuzzySearch, { data: fuzzySearchData }] =
    useLazyFuzzySearchQuery();
  const [performTeamPerformanceSearch, { data: teamPerformanceData }] =
    useLazyTeamPerformanceSearchQuery();

  const list = useAsyncList<FuzzySearchResult>({
    initialFilterText: searchQuery,
    getKey: (item) => buildKeyForSearchResult(item),
    async load({ filterText }) {
      if (!filterText) {
        return {
          items: [],
        };
      }

      if (props.isTeamPerformanceSearchContext) {
        // Each section is searched with its own limit, so the results arrive
        // ready to lay out; they are held flat here and split by type below.
        const { teammates, calls } = await performTeamPerformanceSearch({
          query: filterText,
          playbookId: currentPlaybookId,
          limit: teamPerformanceResultCountPerSection,
        }).unwrap();

        return { items: [...teammates, ...calls] };
      }

      return {
        items: await performFuzzySearch({
          query: filterText,
          resourceTypes: props.resourceTypes,
        }).unwrap(),
      };
    },
  });

  // Because list.setFilterText is not stable
  const handleFilterTextChange = useEffectEvent((newFilterText: string) => {
    list.setFilterText(newFilterText);
  });

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      handleFilterTextChange(searchQuery.trim());
    }, searchQueryDebounceDuration);

    return () => {
      clearTimeout(handler);
    };
  }, [handleFilterTextChange, searchQuery]);

  const handleSearchResultsLoad = useEffectEvent(() => {
    props.onSearchResultsLoad(list.items);
  });

  const isLoaded =
    (props.isTeamPerformanceSearchContext
      ? teamPerformanceData
      : fuzzySearchData) !== undefined;
  useEffect(() => {
    if (isLoaded) {
      handleSearchResultsLoad();
    }
  }, [isLoaded, handleSearchResultsLoad]);

  const navigate = useNavigate();
  const trimmedQuery = searchQuery.trim();

  // Closing the modal hands focus back to whatever opened it — right for a
  // dismissal, wrong once we've navigated, since the nav trigger is no longer
  // where the user is. Claim focus for the page instead, on unmount: while the
  // modal is still exiting its focus scope contains focus, and react-aria
  // skips its own restore when focus has moved somewhere deliberate by then.
  const shouldFocusDestinationRef = useRef(false);
  useLayoutEffect(
    () => () => {
      if (shouldFocusDestinationRef.current) {
        focusMainContent();
      }
    },
    [],
  );

  const handleSelectResult = (result: FuzzySearchResult) => {
    const to = buildPathForSearchResult(result, {
      isTrainingSearchContext: props.isTrainingSearchContext,
    });
    if (!to) {
      return;
    }

    recordRecentlyOpenedResult(companyId, {
      resource_type: result.resource_type,
      resource_id: result.resource_id,
    });
    shouldFocusDestinationRef.current = true;
    props.close();
    navigate(to);
  };

  const handleSearchAllCalls = (query: string) => {
    // The only recorded searches: recents replay a call keyword search, so
    // opening a result contributes nothing to them.
    recordRecentSearchQuery(companyId, query);
    shouldFocusDestinationRef.current = true;
    props.close();
    // Outside a playbook route (the agents section also searches team
    // performance) fall back to the placeholder segment, which resolves to
    // their default playbook — at the cost of a redirect that remounts the app
    // chrome.
    const playbookId = currentPlaybookId ?? DEFAULT_PLAYBOOK_URL_SEGMENT;
    const callsPath =
      props.transcriptSearchTeammateId === undefined
        ? appPaths.playbookCalls.build({ playbookId })
        : appPaths.teammateCalls.build({
            playbookId,
            teammateId: props.transcriptSearchTeammateId,
          });

    navigate(
      `${callsPath}?q=${encodeURIComponent(query)}`,
      // Re-applies the query even when the URL is unchanged (e.g. searching
      // for the same phrase again after editing the calls-page input).
      { state: buildTranscriptSearchApplyState() },
    );
  };

  // History is read once per open (the dialog mounts fresh each time), so the
  // lists don't reshuffle while the modal is on screen.
  const [recentSearchQueries] = useState(() =>
    props.showTranscriptKeywordSearch
      ? readRecentSearchQueries(companyId).slice(0, visibleHistoryEntryCount)
      : [],
  );
  const [recentlyOpenedKeys] = useState(() =>
    readRecentlyOpenedResultKeys(companyId)
      .filter(
        (key) =>
          props.resourceTypes === undefined ||
          props.resourceTypes.includes(key.resource_type),
      )
      .slice(0, visibleHistoryEntryCount),
  );
  const { data: recentlyOpenedResults } = useSearchResourcesQuery(
    recentlyOpenedKeys.length > 0
      ? { resourceKeys: recentlyOpenedKeys }
      : skipToken,
  );

  // Names the playbook a teammate row points at, for the searches that can
  // return one row per playbook (see `buildSearchResultSubtitle`). Cheap: this
  // list is already cached app-wide.
  const { data: minimalPlaybooks } = useAllMinimalPlaybooksQuery(
    props.isTeamPerformanceSearchContext ? skipToken : undefined,
  );
  const playbookNamesById = useMemo(
    () =>
      new Map(
        (minimalPlaybooks ?? []).map((playbook) => [
          playbook.id,
          playbook.name,
        ]),
      ),
    [minimalPlaybooks],
  );

  const recentSearches: readonly SearchModalAction[] = recentSearchQueries.map(
    (query) => ({
      id: query,
      title: query,
      icon: <IconSearch className="size-4" />,
      onSelect: () => handleSearchAllCalls(query),
    }),
  );

  const buildResultRow = (result: FuzzySearchResult): SearchModalResult => ({
    id: buildKeyForSearchResult(result),
    title: result.result_primary_text,
    subtitle: props.isTeamPerformanceSearchContext
      ? buildTeamPerformanceSubtitle(result)
      : buildSearchResultSubtitle(result, playbookNamesById),
    badge:
      result.resource_type === FuzzySearchResourceType.TeammateWithGradings
        ? "Teammate"
        : undefined,
    trailing: result.result_timestamp ? (
      <ShortAbsoluteTimestamp timestamp={result.result_timestamp} />
    ) : undefined,
    icon: getSearchResultIcon(result.resource_type),
    onSelect: () => handleSelectResult(result),
  });

  // Only the sections this search context can turn up: a team member searches
  // calls alone, so promising them a People section would be a lie.
  const visibleSections = teamPerformanceSections.filter(
    (section) =>
      props.resourceTypes === undefined ||
      props.resourceTypes.includes(section.resourceType),
  );

  const searchResultSections: readonly SearchModalSectionData[] =
    props.isTeamPerformanceSearchContext
      ? visibleSections.map((section) => ({
          id: section.id,
          title: section.title,
          results: list.items
            .filter((result) => result.resource_type === section.resourceType)
            .map(buildResultRow),
          placeholder: {
            rowCount: teamPerformanceResultCountPerSection,
            hasBadge: section.hasBadge,
            hasTrailing: section.hasTrailing,
          },
        }))
      : [
          {
            id: "results",
            results: list.items.map(buildResultRow),
            // A hint, not a promise: the flat contexts show up to ten matches.
            placeholder: { rowCount: 3 },
          },
        ];

  const sections: readonly SearchModalSectionData[] = trimmedQuery
    ? searchResultSections
    : [
        {
          id: "recently-opened",
          title: "Recently viewed",
          results: (recentlyOpenedResults ?? []).map(buildResultRow),
        },
      ];

  // The rows on screen don't answer the current query: it is either still
  // debouncing or its request is in flight.
  const hasStaleResults =
    Boolean(trimmedQuery) &&
    (trimmedQuery !== list.filterText || list.isLoading);

  const [hasStaleResultsGraceElapsed, setHasStaleResultsGraceElapsed] =
    useState(false);
  useEffect(() => {
    if (!hasStaleResults) {
      setHasStaleResultsGraceElapsed(false);
      return;
    }

    const handler = setTimeout(() => {
      setHasStaleResultsGraceElapsed(true);
    }, staleResultsGraceDuration);

    return () => {
      clearTimeout(handler);
    };
    // Every new query restarts the grace period, so the skeleton shows up once
    // typing settles rather than mid-word. Once shown it stays until results
    // for the current query arrive.
  }, [hasStaleResults, trimmedQuery]);

  // Show the skeleton while a fresh query has nothing to render yet, and again
  // for a refreshing query whose results are taking a while — otherwise stale
  // rows sit there with no sign that newer ones are on the way.
  const isLoading =
    hasStaleResults && (list.items.length === 0 || hasStaleResultsGraceElapsed);

  return (
    <SearchModal
      query={searchQuery}
      placeholder={props.placeholder}
      // Matches the calls page, which rejects anything longer: the all-calls
      // entry point hands this query straight to it via `?q=`.
      maxLength={MAX_TRANSCRIPT_SEARCH_CHARS}
      sections={sections}
      recentSearches={recentSearches}
      allResultsAction={
        props.showTranscriptKeywordSearch && trimmedQuery
          ? {
              id: "search-all-calls",
              title: `Search all calls for "${trimmedQuery}"`,
              subtitle: "Keyword and meaning across transcripts",
              icon: <IconSearch className="size-4" />,
              onSelect: () => handleSearchAllCalls(trimmedQuery),
            }
          : undefined
      }
      isLoading={isLoading}
      // With the all-calls action on screen there is always a next step, so a
      // "no results" message would be a dead end; the Calls page reports the
      // empty result set instead.
      noResultsState={
        props.showTranscriptKeywordSearch
          ? undefined
          : {
              title: "No results found",
              description: "Try a different phrase.",
            }
      }
      className="max-h-[calc(100vh-4rem)] w-full"
      onQueryChange={setSearchQuery}
    />
  );
};

/**
 * The team performance rows the design spec covers: a call names the teammate
 * who took it and how long it ran, in place of the customer's email or phone,
 * and a teammate keeps their email. A call indexed before durations were shows
 * the teammate on its own.
 */
function buildTeamPerformanceSubtitle(
  result: FuzzySearchResult,
): string | undefined {
  const durationSeconds = readFuzzySearchDurationSeconds(result);

  return joinSubtitleParts(
    result.resource_type === FuzzySearchResourceType.TeammateAccountGrading
      ? [
          result.result_category_text,
          // A call whose recordings have no measured length says nothing rather
          // than rendering the "--" a table cell would.
          durationSeconds ? renderCallDuration(durationSeconds) : undefined,
        ]
      : [result.result_category_text, result.result_secondary_text],
  );
}

function buildSearchResultSubtitle(
  result: FuzzySearchResult,
  playbookNamesById: ReadonlyMap<string, string>,
): string | undefined {
  return joinSubtitleParts([
    result.result_category_text,
    result.result_secondary_text,
    // A teammate is indexed once per playbook, so the same person can appear
    // several times outside the team performance search, which collapses them.
    // Naming the playbook tells those rows apart.
    result.resource_type === FuzzySearchResourceType.TeammateWithGradings
      ? playbookNamesById.get(result.search_attributes.playbook_id ?? "")
      : undefined,
  ]);
}

function joinSubtitleParts(
  parts: readonly (string | undefined)[],
): string | undefined {
  return parts.filter(Boolean).join(" · ") || undefined;
}

function getSearchResultIcon(resourceType: FuzzySearchResourceType): ReactNode {
  switch (resourceType) {
    case FuzzySearchResourceType.TeammateAccountGrading:
      return <IconPhone className="size-4" />;
    case FuzzySearchResourceType.TeammateWithGradings:
    case FuzzySearchResourceType.Interview:
      return <IconUser className="size-4" />;
    case FuzzySearchResourceType.PlaybookCriterion:
      return <IconBook2 className="size-4" />;
  }
}
